import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function AdatkezelesiTajekoztato() {
  useDocumentMeta({
    title: "Adatkezelési tájékoztató | Autó-Szerelem",
    description:
      "A Build & Service Kft. (Autó-Szerelem — Séra Autószervíz) GDPR-megfelelő adatkezelési tájékoztatója. Foglalási űrlap, kapcsolatfelvételi adatok kezelése.",
    path: "/adatkezelesi-tajekoztato",
  });

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white"
      data-testid="adatkezeles-page"
    >
      <Navbar />
      <main className="pt-24 sm:pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white mb-10 transition-colors"
            data-testid="adatkezeles-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Vissza a főoldalra
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="m-stripe h-1 w-24" />
            <span
              className="text-[0.72rem] font-bold uppercase tracking-[0.32em] text-zinc-300"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              GDPR / Adatvédelem
            </span>
          </div>

          <h1
            className="font-black uppercase text-white leading-[0.95] tracking-tight mb-6"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2rem, 5vw, 3.75rem)",
            }}
          >
            Adatkezelési{" "}
            <br />
            tájékoztató
          </h1>
          <p className="text-sm text-zinc-400 mb-12">
            Hatályos: 2026. január 1-jétől
          </p>

          <div className="space-y-10 text-zinc-300 leading-relaxed text-sm">
            <Section n="1." title="Az adatkezelő adatai">
              <p>
                Adatkezelő:{" "}
                <strong className="text-white">Build & Service Kft.</strong>{" "}
                (továbbiakban: <em>„Adatkezelő"</em> vagy <em>„Társaság"</em>)
              </p>
              <ul className="mt-3 space-y-1">
                <li>Székhely: 4034 Debrecen, Mészöly Géza u. 3.</li>
                <li>Telephely (műhely): 4031 Debrecen, Vadvirág utca 15/D — 3. kapu</li>
                <li>
                  Cégjegyzékszám: <span className="mono">09-09-026253</span> (Debreceni Törvényszék Cégbírósága)
                </li>
                <li>
                  Adószám: <span className="mono">24992392-2-09</span>
                </li>
                <li>Képviselő: Séra Attila ügyvezető</li>
                <li>
                  Kapcsolat:{" "}
                  <a
                    href="mailto:autoszerelem@gmail.com"
                    className="text-[#0082D2] hover:underline"
                  >
                    autoszerelem@gmail.com
                  </a>{" "}
                  ·{" "}
                  <a
                    href="tel:+36308637476"
                    className="text-[#0082D2] hover:underline"
                  >
                    +36 30 863 7476
                  </a>
                </li>
              </ul>
            </Section>

            <Section n="2." title="A jelen tájékoztató hatálya">
              <p>
                A jelen tájékoztató a{" "}
                <a href="https://autoszerelem.hu" className="text-[#0082D2] hover:underline">
                  autoszerelem.hu
                </a>{" "}
                weboldalon (továbbiakban: <em>„Weboldal"</em>) megvalósuló
                adatkezelésekre vonatkozik, így különösen az{" "}
                <strong className="text-white">időpontfoglalási űrlap</strong>,
                a{" "}
                <strong className="text-white">kapcsolatfelvételi űrlap</strong>{" "}
                és az ezek nyomán létrejövő ügyfélkommunikáció során kezelt
                személyes adatokra.
              </p>
              <p className="mt-3">
                Az adatkezelés az Európai Parlament és a Tanács{" "}
                <strong>2016/679 rendelete (GDPR)</strong>, valamint az{" "}
                <strong>
                  információs önrendelkezési jogról és az
                  információszabadságról szóló 2011. évi CXII. törvény (Info tv.)
                </strong>{" "}
                alapján történik.
              </p>
            </Section>

            <Section n="3." title="Kezelt adatok köre, célok és jogalap">
              <h3 className="font-bold text-white mt-3 mb-2">
                3.1. Időpontfoglalási űrlap
              </h3>
              <ul className="space-y-1">
                <li>
                  <strong>Adatok:</strong> név, e-mail cím, telefonszám,
                  gépjármű típusa/évjárata, választott szolgáltatás, kívánt
                  időpont, megjegyzés (opcionális).
                </li>
                <li>
                  <strong>Cél:</strong> az időpontfoglalás kezelése, az
                  ügyféllel való kapcsolatfelvétel, a szervízszolgáltatás
                  előkészítése.
                </li>
                <li>
                  <strong>Jogalap:</strong> az érintett hozzájárulása (GDPR 6.
                  cikk (1) a) pont) és a szerződéskötést megelőző intézkedések
                  megtétele (GDPR 6. cikk (1) b) pont).
                </li>
                <li>
                  <strong>Megőrzés:</strong> a foglalás teljesülésétől
                  számított <strong>5 év</strong> (a számviteli törvény
                  szerinti elszámolás céljából), vagy az érintett törlési
                  kérelméig.
                </li>
              </ul>

              <h3 className="font-bold text-white mt-5 mb-2">
                3.2. Kapcsolatfelvételi űrlap
              </h3>
              <ul className="space-y-1">
                <li>
                  <strong>Adatok:</strong> név, e-mail cím, üzenet szövege.
                </li>
                <li>
                  <strong>Cél:</strong> az érintett kérdésének megválaszolása,
                  kapcsolatfelvétel.
                </li>
                <li>
                  <strong>Jogalap:</strong> az érintett hozzájárulása (GDPR 6.
                  cikk (1) a) pont).
                </li>
                <li>
                  <strong>Megőrzés:</strong> a megkeresés lezárásától
                  számított <strong>1 év</strong>, vagy a hozzájárulás
                  visszavonásáig.
                </li>
              </ul>

              <h3 className="font-bold text-white mt-5 mb-2">
                3.3. Sütik (cookies)
              </h3>
              <p>
                A weboldal a működéséhez szükséges (funkcionális) sütiket
                használ a Cloudflare CDN-en keresztül (pl. <span className="mono">__cf_bm</span>{" "}
                — bot-szűrés). Marketing- és nyomkövető sütiket nem
                alkalmazunk. Külső szolgáltatóként a webfontok betöltése a
                Google Fonts-ról történik.
              </p>
            </Section>

            <Section n="4." title="Adatfeldolgozók (technikai partnerek)">
              <ul className="space-y-2">
                <li>
                  <strong className="text-white">Emergent</strong> (emergent.sh) —
                  weboldal hosztolás, alkalmazás-infrastruktúra
                </li>
                <li>
                  <strong className="text-white">Cloudflare, Inc.</strong> —
                  CDN, DDoS-védelem, SSL tanúsítvány
                </li>
                <li>
                  <strong className="text-white">Rackhost Zrt.</strong> (6722 Szeged,
                  Tisza Lajos krt. 41.) — domain regisztráció, DNS
                </li>
                <li>
                  <strong className="text-white">MongoDB Inc.</strong> /
                  hostolt adatbázis-szolgáltatás — a foglalási és üzenet
                  rekordok tárolása
                </li>
              </ul>
              <p className="mt-3">
                Az adatfeldolgozók kizárólag a tárolási és továbbítási
                infrastruktúrát biztosítják; az adatokat önállóan nem
                használják fel.
              </p>
            </Section>

            <Section n="5." title="Adatbiztonság">
              <p>
                Az Adatkezelő a tudomány és technika állása szerinti megfelelő
                technikai és szervezési intézkedéseket alkalmazza az adatok
                biztonsága érdekében: HTTPS-titkosítás (TLS 1.3), jelszavak
                hashelt tárolása (bcrypt), korlátozott admin-hozzáférés,
                szerveroldali validáció.
              </p>
            </Section>

            <Section n="6." title="Az érintett jogai">
              <p>A GDPR alapján Önt — mint érintettet — az alábbi jogok illetik meg:</p>
              <ul className="mt-3 space-y-1 list-disc pl-5">
                <li>
                  <strong>Hozzáférési jog</strong> — tájékoztatás kérése a
                  kezelt adatokról
                </li>
                <li>
                  <strong>Helyesbítési jog</strong> — pontatlan adat javítása
                </li>
                <li>
                  <strong>Törlési jog („elfeledtetéshez való jog")</strong> —
                  meghatározott esetekben
                </li>
                <li>
                  <strong>Adatkezelés korlátozásához való jog</strong>
                </li>
                <li>
                  <strong>Adathordozhatósághoz való jog</strong>
                </li>
                <li>
                  <strong>Tiltakozás joga</strong>
                </li>
                <li>
                  <strong>Hozzájárulás visszavonása</strong> — a jövőre nézve,
                  bármikor
                </li>
              </ul>
              <p className="mt-3">
                Jogai gyakorlása érdekében forduljon hozzánk a fenti
                elérhetőségeken. Kérelmét legfeljebb <strong>30 napon belül</strong>{" "}
                megválaszoljuk.
              </p>
            </Section>

            <Section n="7." title="Jogorvoslat — felügyeleti hatóság">
              <p>
                Amennyiben úgy ítéli meg, hogy az adatkezelés a GDPR-ba ütközik,
                panasszal élhet a felügyeleti hatóságnál:
              </p>
              <div className="mt-3 p-4 border border-white/10 bg-[#0e0e0e]">
                <strong className="text-white">
                  Nemzeti Adatvédelmi és Információszabadság Hatóság (NAIH)
                </strong>
                <ul className="mt-2 text-sm space-y-1">
                  <li>1055 Budapest, Falk Miksa utca 9–11.</li>
                  <li>Postacím: 1363 Budapest, Pf. 9.</li>
                  <li>Telefon: +36 (1) 391-1400</li>
                  <li>
                    E-mail:{" "}
                    <a
                      href="mailto:ugyfelszolgalat@naih.hu"
                      className="text-[#0082D2] hover:underline"
                    >
                      ugyfelszolgalat@naih.hu
                    </a>
                  </li>
                  <li>
                    Web:{" "}
                    <a
                      href="https://naih.hu"
                      className="text-[#0082D2] hover:underline"
                    >
                      naih.hu
                    </a>
                  </li>
                </ul>
              </div>
              <p className="mt-3">
                Bírósági jogorvoslat: az érintett a lakóhelye vagy tartózkodási
                helye szerinti törvényszék előtt is érvényesítheti igényét.
              </p>
            </Section>

            <Section n="8." title="A tájékoztató módosítása">
              <p>
                Az Adatkezelő fenntartja a jogot, hogy a jelen tájékoztatót
                indokolt esetben — különösen jogszabályi változás vagy az
                adatkezelési gyakorlat változása esetén — egyoldalúan
                módosítsa. A módosítás a weboldalon való közzététellel lép
                hatályba.
              </p>
            </Section>

            <div className="pt-8 border-t border-white/10 text-xs text-zinc-500">
              Hatálybalépés: 2026. január 1. · Utolsó frissítés: 2026. január
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section>
      <h2
        className="font-bold uppercase text-white tracking-tight mb-4 pb-3 border-b border-white/10"
        style={{ fontFamily: "Barlow Condensed", fontSize: "clamp(1.25rem, 2.4vw, 1.65rem)" }}
      >
        <span className="text-[#0082D2] mr-3">{n}</span>
        {title}
      </h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
